import { useContext, useLayoutEffect, useRef, useState } from "react"
import { Button, Col, Div, Row, Text } from "atomize"
import { toast } from "react-toastify"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import Container from "../atoms/Container"
import Paragraph from "../atoms/Paragraph"
import FocusAreasPicker from "../lesson/FocusAreasPicker"
import LessonSummary from "../lesson/LessonSummary"
import { theme } from "../settings/config"
import { modalContext } from "../providers/modal.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import { editLessonContent } from "../utils/firestore.utils"
import { getLessonColor, getLessonFillColor } from "../utils/func.utils"
import {
	DIARY_MAX_TEXT,
	PLAYER_NOTE_MAX_TEXT,
	chosenAreas,
	normalizeLessonContent,
	notePlayers,
} from "../utils/lessonContent.utils"

// Un testo che cresce con quello che ci si scrive: bianco sul fondo caldo
// del foglio, come gli esercizi del programma
function NoteField({ id, value, onChange, placeholder, maxLength, disabled, minRows = 2 }) {
	const ref = useRef(null)
	useLayoutEffect(() => {
		const textarea = ref.current
		if (!textarea) return
		textarea.style.height = "auto"
		textarea.style.height = `${textarea.scrollHeight}px`
	}, [value])

	return (
		<textarea
			ref={ref}
			id={id}
			rows={minRows}
			value={value}
			placeholder={placeholder}
			maxLength={maxLength}
			disabled={disabled}
			onChange={e => onChange(e.target.value)}
			style={{
				display: "block",
				width: "100%",
				resize: "none",
				overflow: "hidden",
				boxSizing: "border-box",
				border: 0,
				borderRadius: "15px",
				padding: "0.625rem 0.75rem",
				background: theme.colors.surface,
				font: "inherit",
				lineHeight: 1.45,
				color: theme.colors.text,
			}}
		/>
	)
}

// Un allievo da scegliere: acceso ha il pieno del tipo di lezione, e il
// pallino dice che su di lui c'è già una riga
function PlayerChip({ label, isSelected, hasNote, color, disabled, onClick }) {
	return (
		<button
			type="button"
			aria-pressed={isSelected}
			aria-label={hasNote ? `${label}, c'è già una riga` : label}
			disabled={disabled}
			onClick={onClick}
			style={{
				position: "relative",
				padding: "0.25rem 0.75rem",
				borderRadius: "15px",
				border: `2px solid ${isSelected ? color : theme.colors.surface}`,
				background: isSelected ? color : theme.colors.background,
				color: isSelected ? theme.colors.white : theme.colors.text,
				font: "inherit",
				fontWeight: isSelected ? 700 : 400,
				cursor: disabled ? "default" : "pointer",
			}}>
			{label}
			{hasNote && !isSelected && (
				<span
					aria-hidden="true"
					style={{
						position: "absolute",
						top: "-3px",
						right: "-3px",
						width: "10px",
						height: "10px",
						borderRadius: "50%",
						background: color,
						border: `2px solid ${theme.colors.surface}`,
					}}
				/>
			)}
		</button>
	)
}

// "Com'è andata?": le note veloci a fine lezione (#166). Si apre dalla
// lezione appena finita in agenda e dal riquadro Programma di una lezione
// finita. Tre cose, tutte facoltative:
// - su cosa avete lavorato, per macroaree, accese dal programma se c'era;
// - il diario, due righe su tutta la lezione;
// - una riga su qualche allievo, che lui ritrova nelle sue lezioni.
// Il resto del programma non si tocca, e da qui si apre per intero.
export function LessonDiaryModal({ closeModal, lessonData }) {
	const Modal = useContext(modalContext)
	const { getLessonType } = useContext(sharedDataContext)

	const [content, setContent] = useState(() => normalizeLessonContent(lessonData.lessonContent))
	const [isSaving, setSaving] = useState(false)
	const players = notePlayers(lessonData.partecipants)
	// si parte dal primo che ha già una riga, se c'è
	const [selectedID, setSelectedID] = useState(
		() => players.find(player => content.playerNotes?.[player.playerID])?.playerID ?? null,
	)
	const hadAreas = chosenAreas(lessonData.lessonContent).length > 0
	const playerColor = getLessonFillColor(getLessonType(lessonData.lessonType))
	const selected = players.find(player => player.playerID === selectedID)

	const update = changes => setContent(current => ({ ...current, ...changes }))
	// la riga su un allievo, e dove la ritroverà
	const playerNoteField = player => (
		<>
			<NoteField
				// un campo per allievo: cambiando allievo si riparte da capo
				key={player.playerID}
				id="lesson-player-note"
				value={content.playerNotes?.[player.playerID] ?? ""}
				placeholder={`Una riga su ${player.label}`}
				maxLength={PLAYER_NOTE_MAX_TEXT}
				disabled={isSaving}
				onChange={text => setPlayerNote(player.playerID, text)}
			/>
			<Text textSize="caption" textColor={theme.colors.text_light} m={{ t: "0.375rem" }}>
				La ritrovi nelle lezioni di {player.label}, sotto questa.
			</Text>
		</>
	)
	const setPlayerNote = (playerID, text) =>
		setContent(current => ({
			...current,
			playerNotes: { ...current.playerNotes, [playerID]: text },
		}))

	const save = async () => {
		setSaving(true)
		try {
			await editLessonContent(lessonData.lessonID, content)
		} catch {
			toast.error("Le note della lezione non sono state salvate", {
				position: "top-center",
			})
			setSaving(false)
			return
		}
		closeModal()
	}

	return (
		<>
			<Container>
				<Div textWeight="bold" textSize="title" textColor={theme.colors.text}>
					Com'è andata?
				</Div>
				<Div m={{ b: "0.75rem" }}>
					<LessonSummary lessonData={lessonData} />
				</Div>
			</Container>
			<Div maxH="60vh" style={{ overflowY: "auto", overflowX: "hidden" }}>
				<Container d="flex" flexDir="column" style={{ gap: "1.25rem" }}>
					{/* un blocco per parte: il Paragraph i suoi figli non li avvolge */}
					<Div>
						<Paragraph title="Su cosa avete lavorato:" m={{ b: "0.5rem" }}>
							<FocusAreasPicker
								value={content}
								onChange={update}
								disabled={isSaving}
								withDetail={false}
							/>
							{hadAreas && (
								<Text textSize="caption" textColor={theme.colors.text_light} m={{ t: "0.5rem" }}>
									Dal programma: togli quello che non avete fatto.
								</Text>
							)}
						</Paragraph>
					</Div>

					<Div>
						<label htmlFor="lesson-diary" style={{ display: "block", marginBottom: "0.5rem" }}>
							<Text textSize="subheader" textWeight="bold">
								Diario:
							</Text>
						</label>
						<NoteField
							id="lesson-diary"
							value={content.diary ?? ""}
							placeholder="Cosa avete fatto, cosa resta da fare"
							maxLength={DIARY_MAX_TEXT}
							disabled={isSaving}
							minRows={3}
							onChange={diary => update({ diary })}
						/>
					</Div>

					{/* uno solo: è lui, non c'è da scegliere */}
					{players.length === 1 && (
						<Div>
							<label
								htmlFor="lesson-player-note"
								style={{ display: "block", marginBottom: "0.5rem" }}>
								<Text textSize="subheader" textWeight="bold">
									Su {players[0].label}:
								</Text>
							</label>
							{playerNoteField(players[0])}
						</Div>
					)}

					{players.length > 1 && (
						<Div>
							<Paragraph title="Su qualcuno in particolare?" m={{ b: "0.5rem" }}>
								<Div d="flex" flexWrap="wrap" style={{ gap: "0.5rem" }}>
									{players.map(player => (
										<PlayerChip
											key={player.playerID}
											label={player.label}
											isSelected={player.playerID === selectedID}
											hasNote={Boolean(content.playerNotes?.[player.playerID]?.trim())}
											color={playerColor}
											disabled={isSaving}
											onClick={() =>
												setSelectedID(id => (id === player.playerID ? null : player.playerID))
											}
										/>
									))}
								</Div>
								{selected && (
									<>
										<label
											htmlFor="lesson-player-note"
											style={{ display: "block", margin: "0.75rem 0 0.25rem" }}>
											<Text
												textWeight="bold"
												textColor={getLessonColor(getLessonType(lessonData.lessonType), true)}>
												{selected.label}:
											</Text>
										</label>
										{playerNoteField(selected)}
									</>
								)}
							</Paragraph>
						</Div>
					)}

					<Button
						h="2.75rem"
						p="0"
						m={{ t: "-0.75rem" }}
						bg="transparent"
						textColor={theme.colors.primary}
						textWeight="600"
						d="inline-flex"
						style={{ alignSelf: "flex-start" }}
						disabled={isSaving}
						// quello scritto qui non si perde: il foglio intero parte da qui
						onClick={() => Modal.open("lessonContent", { ...lessonData, lessonContent: content })}>
						Apri tutto il programma
					</Button>
				</Container>
			</Div>
			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					<Col p="0">
						<SecondaryButton
							m="auto"
							textColor={theme.colors.gunmetal}
							text="Annulla"
							textSize="subheader"
							disabled={isSaving}
							action={closeModal}
						/>
					</Col>
					<Col p="0">
						<PrimaryButton
							m="auto"
							bg={theme.colors.primary}
							textColor="white"
							textSize="subheader"
							text={isSaving ? "Salvo..." : "Salva"}
							disabled={isSaving}
							action={save}
						/>
					</Col>
				</Row>
			</Div>
		</>
	)
}

export default LessonDiaryModal
